// PageAgentButton — the one button a page mounts to open its helper (Douglas
// plan T8). The helper is the agent, so it needs agent access (D8: the
// agent_sessions feature). Below that level the button is shown disabled with
// the standard sentence and who to ask (utils/permissionReason.ts), never
// hidden.

import React from 'react'
import { Button } from '@mui/material'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import DisabledReason from '../../components/system/DisabledReason'
import useAiAccess from '../../hooks/useAiAccess'
import useWhoToAsk from '../../hooks/useWhoToAsk'
import { refuse } from '../../utils/permissionReason'
import { PAGE_HELPERS } from '../pageHelpers.js'

export interface PageAgentButtonProps {
  /** A PAGE_HELPERS key, e.g. `tables`. */
  pageKey: string
  /** The customer NAME (account.customer.name): whose AI access decides. */
  customer: string
  onClick: () => void
  /** The pane is open: the button reads as pressed. */
  open?: boolean
  /** The button's words. Default "<Title> helper". */
  label?: string
}

export default function PageAgentButton({ pageKey, customer, onClick, open = false, label }: PageAgentButtonProps) {
  const aiAccess = useAiAccess(customer)
  const who = useWhoToAsk(customer)
  const title = PAGE_HELPERS.find(h => h.key === pageKey)?.title ?? pageKey
  const reason = aiAccess.levelAllows('agent_sessions')
    ? undefined
    : refuse(`You can't use the ${title} helper without agent access.`, who).reason

  return (
    <DisabledReason reason={reason}>
      <Button
        size="small"
        color="info"
        variant={open ? 'contained' : 'outlined'}
        disableElevation
        aria-pressed={open}
        startIcon={<AutoAwesomeOutlinedIcon fontSize="small" />}
        onClick={onClick}
        sx={{ textTransform: 'none' }}
      >
        {label ?? `${title} helper`}
      </Button>
    </DisabledReason>
  )
}
